import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { ArrowRight, Linkedin } from "lucide-react";

const ROLE_LABELS = {
  director_general: "Dirección General",
  socio: "Socios Fundadores",
  consejero: "Consejo Académico",
  instructor: "Instructor",
  staff: "Staff",
};

const ROLE_ORDER = ["director_general", "socio", "consejero", "instructor", "staff"];

function initialsOf(name) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

function MemberAvatar({ member, size = 96 }) {
  const style = { width: size, height: size, backgroundColor: member.initials_color_hex || "#F26A21" };
  if (member.photo_url) {
    return (
      <img
        src={member.photo_url}
        alt={member.name}
        style={style}
        className="rounded-full object-cover border-2 border-white shadow-md"
      />
    );
  }
  return (
    <div
      style={style}
      className="rounded-full flex items-center justify-center text-white font-bold border-2 border-white shadow-md"
    >
      <span style={{ fontSize: size * 0.35 }}>{initialsOf(member.name)}</span>
    </div>
  );
}

export default function Equipo() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/public/team")
      .then((r) => setMembers(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Group by role_category
  const grouped = ROLE_ORDER.map((cat) => ({
    cat,
    label: ROLE_LABELS[cat],
    items: members.filter((m) => m.role_category === cat),
  })).filter((g) => g.items.length > 0);

  return (
    <PublicLayout>
      <section className="relative overflow-hidden bg-[#0B0B0B] text-white">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#F26A21] bg-[#F26A21]/10 border border-[#F26A21]/40 rounded-full px-3 py-1">
            Equipo directivo y socios
          </p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] font-display">
            Las personas detrás<br />
            <span className="text-[#F26A21]">de Debt Capital College</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-gray-300 leading-relaxed">
            Un equipo con décadas de trayectoria en banca corporativa, mercados de capitales,
            regulación y formación ejecutiva. Diseñamos y operamos el ecosistema DCC junto
            con nuestros socios y consejo académico.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {loading && <p className="text-gray-500">Cargando...</p>}
        {!loading && grouped.length === 0 && (
          <p className="text-gray-500" data-testid="team-empty">Aún no hay miembros publicados.</p>
        )}
        {grouped.map((group) => (
          <div key={group.cat} data-testid={`team-group-${group.cat}`}>
            <div className="flex items-end justify-between flex-wrap gap-2 mb-8">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#F26A21] font-semibold">{group.label}</p>
                <h2 className="mt-1 text-2xl md:text-3xl font-bold text-[#0B0B0B] font-display">
                  {group.cat === "director_general" && "Dirección general"}
                  {group.cat === "socio" && "Socios fundadores"}
                  {group.cat === "consejero" && "Consejo académico"}
                  {group.cat === "instructor" && "Instructores"}
                  {group.cat === "staff" && "Staff DCC"}
                </h2>
              </div>
            </div>
            <div className={`grid gap-6 ${group.cat === "director_general" ? "md:grid-cols-1 max-w-3xl" : "md:grid-cols-2 lg:grid-cols-3"}`}>
              {group.items.map((m) => (
                <Link
                  key={m.id}
                  to={`/equipo/${m.slug}`}
                  data-testid={`team-card-${m.slug}`}
                  className="group p-6 bg-[#F9FAFB] border border-gray-200 rounded-xl hover:border-[#F26A21] hover:-translate-y-0.5 transition-all flex gap-5 items-start"
                >
                  <MemberAvatar member={m} size={group.cat === "director_general" ? 128 : 88} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-[#F26A21] font-semibold">
                      {m.role_title}
                    </p>
                    <h3 className="mt-1 text-xl font-bold text-[#0B0B0B] font-display group-hover:text-[#F26A21] transition-colors">
                      {m.name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-700 leading-relaxed line-clamp-4">
                      {m.short_bio}
                    </p>
                    <div className="mt-3 flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1 text-[#F26A21] font-semibold group-hover:gap-1.5 transition-all">
                        Ver biografía <ArrowRight className="w-3 h-3" />
                      </span>
                      {m.linkedin_url && (
                        <a
                          href={m.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-gray-500 hover:text-[#F26A21]"
                          aria-label={`LinkedIn de ${m.name}`}
                        >
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
    </PublicLayout>
  );
}
